import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  Box,
  Grid,
  Container,
  Typography,
  useTheme,
  Stack,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Button,
  Card,
} from "@mui/material";
import { alpha } from "@mui/material/styles";

// Iconify icons
import { Icon } from "@iconify/react";
import alertTriangleFill from "@iconify/icons-eva/alert-triangle-fill";
import clockOutline from "@iconify/icons-eva/clock-outline";
// components
import Page from "../components/Page";
// import { DashboardCard } from "../components/_dashboard/app"; // unused
import { useAuth } from "src/hooks/useAuth.hook";
import { dashboardService } from "src/services";
import { useSelectedTeam } from "src/hooks/useSelectedTeam";
import { useGetDetail, useList } from "src/hooks/useFetch.hook";
import AdvancedMapView from "src/components/map/AdvancedMapView";
import {
  DashboardDonutStatsCard,
  DashboardNumberStatsCard,
  DashboardMapCard,
  StyledFormControl,
} from "src/components/_dashboard/app/DashboardCard";
import UsageBarChartCard from "src/components/UsageBarChartCard";
import {
  STATISTICS_URL,
  addDuplicatesValues,
  customDateDropdownValidationSchema,
  dashboardCards,
  requiredDateFormatForGraphXaxis,
  statisticValueFormat,
} from "./dashboardConfig";
import { Form, FormikProvider, useFormik } from "formik";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  dashboardDateFormatter,
  isValidTimeInterval,
  timeIntervalFormatter,
} from "src/utils/formatTime";
import { is } from "src/util/is";
import { alertSlice } from "src/store/alert/alert.slice";
import { useDispatch } from "react-redux";
import enums from "src/util/enums";
import { useNavigate } from "react-router-dom";
import { teamsSlice } from "src/store/teams/teams.slice";

const DashboardAppNew = () => {
  const { user } = useAuth();
  const { data: chargers, isFetching: chargersListFetching } = useList(
    "chargers",
    true,
    { c: false }
  );
  const topDropdownValues = dashboardCards.get("topDropdownValues") || [];
  const statsCardTemplatesRef = useRef([...dashboardCards.get("statisticCards")]);
  const defaultTimeframeOption = useMemo(
    () => topDropdownValues.find((option) => option.selected) ?? topDropdownValues[0],
    [topDropdownValues]
  );
  const defaultTimeframeValue = defaultTimeframeOption?.value ?? "Current";
  const buildDefaultInterval = useCallback(
    () => ({
      ...timeIntervalFormatter(new Date(), 0),
      type: defaultTimeframeValue,
    }),
    [defaultTimeframeValue]
  );
  // Stable memoized chargers array to avoid prop identity churn causing downstream re-renders
  const memoizedChargers = useMemo(() => {
    const raw = chargers?.chargers || chargers;
    if (!Array.isArray(raw)) return [];
    return raw.map(c => c); // shallow copy once
  }, [chargers]);
  const theme = useTheme();
  const cardSurface = useMemo(
    () =>
      theme.palette.mode === "light"
        ? theme.palette.background.paper
        : theme.palette.background.elevated,
    [theme.palette.mode]
  );
  const controlSurface = useMemo(
    () =>
      theme.palette.mode === "light"
        ? theme.palette.background.paper
        : theme.palette.background.neutral,
    [theme.palette.mode]
  );
  const supportTextColor = useMemo(
    () => (theme.palette.mode === "dark" ? alpha("#FFFFFF", 0.85) : "#4f4f4f"),
    [theme.palette.mode]
  );
  const supportMutedColor = useMemo(
    () => (theme.palette.mode === "dark" ? alpha("#FFFFFF", 0.65) : "#636363"),
    [theme.palette.mode]
  );
  const dashboardCanvasStyles = useMemo(() => {
    const isLight = theme.palette.mode === "light";
    return {
      background: theme.palette.gradients.dashboardCanvas,
      borderRadius: "20px",
      padding: { xs: 2, md: 3 },
      border: isLight
        ? "1px solid rgba(255,255,255,0.55)"
        : `1px solid ${alpha(theme.palette.divider, 0.4)}`,
      boxShadow: isLight
        ? "0px 25px 60px rgba(15, 23, 42, 0.08)"
        : "0px 25px 60px rgba(0, 0, 0, 0.35)",
      backdropFilter: "blur(6px)",
    };
  }, [theme]);
  const outlineColor = useMemo(
    () => alpha(theme.palette.divider, theme.palette.mode === "light" ? 0.35 : 0.7),
    [theme.palette.divider, theme.palette.mode]
  );

  const resolvePaletteColor = useCallback(
    (paletteKey, shadeKey) => {
      const paletteSection = paletteKey ? theme.palette[paletteKey] : undefined;
      if (paletteSection && shadeKey && paletteSection[shadeKey]) {
        return paletteSection[shadeKey];
      }
      return theme.palette.text.primary;
    },
    [theme]
  );

  const decorateStatCard = useCallback(
    (card) => {
      const paletteKey = card.paletteKey ?? card.colorTheme;
      const fontColorKey = card.fontColorKey ?? card.fontColor;
      return {
        ...card,
        paletteKey,
        fontColorKey,
        colorTheme: theme.palette[paletteKey] ?? card.colorTheme,
        bgColor: cardSurface,
        fontColor: resolvePaletteColor(paletteKey, fontColorKey),
        countColor: card.countColor ?? theme.palette.text.primary,
      };
    },
    [cardSurface, resolvePaletteColor, theme]
  );

  const { teamId } = useSelectedTeam();

  // Check if selected team is GP (based on company id and type)
  const isSelectedTeamGP = user?.company?.id === teamId && user?.company?.companyType === "GP";

  // API calls for license data (only for non-GP teams)
  const { data: licenseCountData, isFetching: licenseCountFetching } = useList(
    "licensesDetails",
    !isSelectedTeamGP, // Only fetch if selected team is not GP
    { count: true }
  );

  // Extract counts from API response
  const expiredLicenses = licenseCountData?.chargersWithoutLicenseCount || 0;
  const upcomingExpiringLicenses = licenseCountData?.chargersWithUpcomingLicenseExpiryCount || 0;
  const shouldShowLicenseBanner =
    !isSelectedTeamGP && (expiredLicenses > 0 || upcomingExpiringLicenses > 0);

  const {
    actions: { setAlert },
  } = alertSlice;
  const dispatch = useDispatch();
  const [customTimeInterval, setCustomTimeInterval] = useState(() => buildDefaultInterval());
  const [customSelectedTime, setCustomSelectedTime] = useState({});
  const [statsCards, setStatsCards] = useState(() => {
    const cardsArr = statsCardTemplatesRef.current
      .filter((card) => !card.isLive)
      .map((card) => decorateStatCard(card));
    return cardsArr;
  });
  const decorateStatCardRef = useRef(decorateStatCard);
  useEffect(() => {
    decorateStatCardRef.current = decorateStatCard;
  }, [decorateStatCard]);

  const [deviceStats, setDeviceStats] = useState({
    totalDevices: 0,
    onlineDevices: 0,
    offlineDevices: 0,
    isLoading: true,
  });

  useEffect(() => {
    setStatsCards((prev) => prev.map((card) => decorateStatCard(card)));
  }, [decorateStatCard]);

  const [barStatsCard, setBarStatsCard] = useState({
    xaxis: [],
    yaxis: [],
  });
  const cumulativeUsageData = useMemo(() => {
    const xaxis = Array.isArray(barStatsCard.xaxis) ? barStatsCard.xaxis : [];
    const yaxis = Array.isArray(barStatsCard.yaxis) ? barStatsCard.yaxis : [];
    return xaxis.map((time, index) => {
      const rawValue = Number(yaxis[index] ?? 0);
      const normalized = Number.isFinite(rawValue) ? rawValue / 1000 : 0;
      return { time, value: normalized };
    });
  }, [barStatsCard]);
  const [customTimeRangeArr, customTimeRangeInitial] = useMemo(() => {
    const customTimeRangeArr = [...dashboardCards.get("topCustomRangeCard")];
    const customTimeRangeInitial = customTimeRangeArr
      .filter((component) => component.fieldType !== "LABEL")
      .reduce((valObj, component) => {
        const { fieldName, fieldType, value } = component;
        let initialValue = value ?? "";
        if (fieldType === "DROPDOWN") {
          initialValue = defaultTimeframeValue;
        }
        return {
          ...valObj,
          [fieldName]: initialValue,
        };
      }, {});
    return [customTimeRangeArr, customTimeRangeInitial];
  }, [defaultTimeframeValue]);

  const { data: profileData } = useGetDetail(
    "profile",
    { id: 0 },
    { force: true }
  );


  const navigate = useNavigate();

  const handleLicenseStatusClick = () => {
    navigate("/charger");
  };

  const handleContactSupport = () => {
    const email = "zynkasupport@zynka.ai";
    const subject = "License Support Request";
    const body = "Hello Zynka Support Team,\n\nI need assistance with license-related issues on my dashboard.\n\nDetails:\n- Expired Licenses: " + expiredLicenses + "\n- Upcoming Expiring Licenses: " + upcomingExpiringLicenses + "\n\nPlease contact me for further assistance.\n\nThank you.";
    
    window.location.href = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  //ERROR NOTIFICATION
  const errorNotification = () => {
    return dispatch(
      setAlert({
        open: true,
        message: "Please select a valid time interval",
        severity: enums.SEVERITY.ERROR,
      })
    );
  };

  const expiringLicenses = Array.isArray(profileData?.alerts)
    ? profileData.alerts
    : [];

  // Removed errorNotification (inlined alerts in statistics effect)

  //ONSUBMIT
  const onSubmit = (values, { setSubmitting }) => {
    // console.log(values);
    // setSubmitting(false);
  };

  //ONBLUR
  const handleBlurEvent = (key) => {
    setFieldTouched(key, true);
  };

  const formik = useFormik({
    initialValues: customTimeRangeInitial,
    validationSchema: customDateDropdownValidationSchema,
    onSubmit,
  });

  const {
    errors,
    touched,
    handleSubmit,
    getFieldProps,
    setFieldValue,
    values,
    setFieldTouched,
  } = formik;

  const timeDropdownOnChangeHandler = (e) => {
    const selectedTimeFrame = e.target.value;
    let timeInterval = { ...customTimeInterval };
    switch (selectedTimeFrame) {
      case "Current":
        timeInterval = {
          ...timeIntervalFormatter(new Date(), 0),
          type: selectedTimeFrame,
        };
        break;
      case "Week":
        timeInterval = {
          ...timeIntervalFormatter(new Date(), 7),
          type: selectedTimeFrame,
        };
        break;
      case "Month":
        timeInterval = {
          ...timeIntervalFormatter(new Date(), 31),
          type: selectedTimeFrame,
        };
        break;
      case "Year":
        timeInterval = {
          ...timeIntervalFormatter(new Date(), 365),
          type: selectedTimeFrame,
        };
        break;
      case "Custom":
        timeInterval = {
          ...customSelectedTime,
        };
        break;
      default:
        break;
    }
    setCustomTimeInterval(timeInterval);
    setFieldValue("custom_time_dropdown", selectedTimeFrame);
  };

  const timeIntervalTextChangeHandler = (value, fieldName) => {
    if (value && !isNaN(value.getTime())) {
      const formatttedTime = dashboardDateFormatter(value);
      let updatedCustomTimeInterval = { ...customTimeInterval, type: "Custom" };
      if (fieldName === "custom_time_start") {
        updatedCustomTimeInterval = {
          ...updatedCustomTimeInterval,
          start: formatttedTime,
        };
      } else {
        updatedCustomTimeInterval = {
          ...updatedCustomTimeInterval,
          end: formatttedTime,
        };
      }
      setCustomSelectedTime(updatedCustomTimeInterval);
      setCustomTimeInterval(updatedCustomTimeInterval);
      setFieldValue(fieldName, value);
    } else return;
  };

  // Ref to prevent overlapping fetch cycles leading to repeated state updates
  const statsFetchInFlightRef = useRef(false);
  const lastIntervalRef = useRef(null);

  useEffect(() => {
    let didCancel = false;

    // Guard: require team and complete interval
    if (!teamId) return;
    if (
      is.empty(customTimeInterval.start) ||
      is.empty(customTimeInterval.end) ||
      is.empty(customTimeInterval.type)
    ) {
      return;
    }

    // Stable serialized key for interval identity
    const intervalKey = `${teamId}|${customTimeInterval.start}|${customTimeInterval.end}|${customTimeInterval.type}`;

    // Prevent redundant runs when nothing changed and no fetch in progress
    if (lastIntervalRef.current === intervalKey && !statsFetchInFlightRef.current) {
      return;
    }
    // Block re-entrancy while a fetch is active
    if (statsFetchInFlightRef.current) return;

    lastIntervalRef.current = intervalKey;
    statsFetchInFlightRef.current = true;

    const run = async () => {
      try {
        const initialStatsCards = [...dashboardCards.get("statisticCards")]
          .map((card) => decorateStatCard(card));

        // Set fetching state once (avoid mapping multiple times if cards unchanged)
        setStatsCards((prevCards) => {
          // If already in fetching state for all cards, skip update
          const allAlreadyFetching = prevCards.every((c) => c.isFetching);
          if (allAlreadyFetching) return prevCards;
          return prevCards.map((card) => ({ ...card, isFetching: true }));
        });

        // Build fetch promises based only on required descriptor props
        const fetchDescriptors = [
          ...initialStatsCards.map(({ url, field }) => ({ url, field })),
          { url: STATISTICS_URL["CUMULATIVE_ENERGY_USAGE"], field: "cumulativeEnergyUsage" },
        ];

        const responses = await Promise.all(
          fetchDescriptors.map(({ url, field }) =>
            dashboardService.getIndividualStatistics(teamId, url, customTimeInterval, field)
          )
        );

        if (didCancel) {
          return;
        }

        const formattedResponse = responses.reduce((formatted, response) => {
          const { field, ...rest } = response;
          formatted[field] = rest;
          return formatted;
        }, {});

        if (!is.empty(formattedResponse)) {
          const updatedStatsCards = initialStatsCards.map((statsCard) => {
            const source = formattedResponse[statsCard.field] || {};
            return {
              ...statsCard,
              isFetching: false,
              count: !is.nullOrUndefined(source.count)
                ? statisticValueFormat("count", source.count, statsCard.field)
                : 0,
              footer: !is.nullOrUndefined(source.footer)
                ? statisticValueFormat("footer", source.footer)
                : "",
              percentChange: !is.nullOrUndefined(source.percentChange)
                ? statisticValueFormat(
                    "percentChange",
                    source.percentChange,
                    customTimeInterval.type
                  )
                : null,
              icon: !is.nullOrUndefined(source.percentChange)
                ? statisticValueFormat("icon", source.percentChange)
                : "",
            };
          });

          // Only update if cards actually changed (shallow compare counts & percentChange)
          setStatsCards((prevCards) => {
            if (
              prevCards.length === updatedStatsCards.length &&
              prevCards.every((prev, idx) => {
                const next = updatedStatsCards[idx];
                return (
                  prev.count === next.count &&
                  prev.footer === next.footer &&
                  prev.percentChange === next.percentChange &&
                  prev.icon === next.icon &&
                  prev.isFetching === next.isFetching
                );
              })
            ) {
              return prevCards; // skip redundant state set
            }
            return updatedStatsCards;
          });

          // Bar graph stats
          const dateFormat = requiredDateFormatForGraphXaxis(customTimeInterval);
          const cumulative = formattedResponse["cumulativeEnergyUsage"]?.cumulativeEnergyUsage || [];
          const updatedBarStatsCard = cumulative.reduce(
            (acc, { energyConsumption, chargingEnd }) => {
              acc.xaxis.push(
                dashboardDateFormatter(new Date(chargingEnd), dateFormat)
              );
              acc.yaxis.push(energyConsumption);
              return acc;
            },
            { xaxis: [], yaxis: [] }
          );
          const formattedStats = addDuplicatesValues(updatedBarStatsCard);
          setBarStatsCard((prev) => {
            const same =
              prev.xaxis.length === formattedStats.xaxis.length &&
              prev.yaxis.length === formattedStats.yaxis.length &&
              prev.xaxis.every((v, i) => v === formattedStats.xaxis[i]) &&
              prev.yaxis.every((v, i) => v === formattedStats.yaxis[i]);
            return same ? prev : formattedStats;
          });
        }
      } catch (err) {
        console.error(err);
        // Inline error notification (avoid depending on errorNotification callback)
        dispatch(
          setAlert({
            open: true,
            message: "Failed to load dashboard statistics",
            severity: enums.SEVERITY.ERROR,
          })
        );
      } finally {
        statsFetchInFlightRef.current = false;
      }
    };

    if (customTimeInterval.type === "Custom" && !isValidTimeInterval(customTimeInterval)) {
      // Use direct dispatch for invalid interval (no fetch)
      dispatch(
        setAlert({
          open: true,
          message: "Please select a valid time interval",
          severity: enums.SEVERITY.ERROR,
        })
      );
      statsFetchInFlightRef.current = false;
      return;
    }

    run();
    return () => {
      didCancel = true;
    };
    // Note: intentionally exclude theme.palette / dispatch / setAlert from deps to avoid identity churn loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customTimeInterval.start, customTimeInterval.end, customTimeInterval.type, teamId]);

  useEffect(() => {
    if (chargersListFetching) {
      setDeviceStats((prev) => ({ ...prev, isLoading: true }));
      return;
    }

    const source = chargers?.chargers || chargers;
    const aggregate = { totalDevices: 0, onlineDevices: 0, offlineDevices: 0 };

    if (Array.isArray(source)) {
      for (const charger of source) {
        aggregate.totalDevices += 1;
        if (charger.status === "Active") {
          aggregate.onlineDevices += 1;
        } else {
          aggregate.offlineDevices += 1;
        }
      }
    }

    const nextState = { ...aggregate, isLoading: false };

    setDeviceStats((prev) => {
      if (
        prev.totalDevices === nextState.totalDevices &&
        prev.onlineDevices === nextState.onlineDevices &&
        prev.offlineDevices === nextState.offlineDevices &&
        prev.isLoading === nextState.isLoading
      ) {
        return prev;
      }
      return nextState;
    });
  }, [chargers, chargersListFetching]);

  useEffect(() => {
    if (!teamId) return;

    const nextInterval = buildDefaultInterval();

    setFieldValue("custom_time_dropdown", defaultTimeframeValue);
    setFieldValue("custom_time_start", "");
    setFieldValue("custom_time_end", "");
    setCustomSelectedTime({});
    setCustomTimeInterval(nextInterval);
    setDeviceStats((prev) =>
      prev.isLoading ? prev : { ...prev, isLoading: true }
    );
  }, [teamId, buildDefaultInterval, defaultTimeframeValue, setFieldValue]);

  useEffect(() => {
    if (!teamId) return;
    lastIntervalRef.current = null;
    setStatsCards((prev) => prev.map((card) => ({ ...card, isFetching: true })));
    setBarStatsCard({ xaxis: [], yaxis: [] });
  }, [teamId]);

  // Update statsCards based on time filter to show/hide isLive cards
  useEffect(() => {
    const currentTimeFilter = values["custom_time_dropdown"] || "Current";
    const baseCards = statsCardTemplatesRef.current;

    const filteredCards = currentTimeFilter === "Current"
      ? baseCards
      : baseCards.filter((card) => !card.isLive);

    const decorator = decorateStatCardRef.current;
    const cardsArr = filteredCards.map((card) => ({
      ...decorator(card),
      isFetching: true,
    }));

    setStatsCards(cardsArr);
    setBarStatsCard({ xaxis: [], yaxis: [] });
  }, [values["custom_time_dropdown"]]);

  return (
    <Page
      title="Dashboard | Smart-CMS"
      sx={{ backgroundColor: theme.palette.background.default, minHeight: "100vh" }}
    >
      <Container
        maxWidth="xl"
        sx={{
          "@media screen and (min-width:600px)": { padding: 0 },
          paddingLeft: 0,
          paddingRight: 0,
          backgroundColor: theme.palette.background.default,
        }}
      >
        <Stack
          sx={{
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 2,
            gap: { xs: 2, md: 0 },
          }}
        >
          <Box
            sx={{
              pb: { xs: "2", sm: "4" },
              textAlign: { xs: "center", sm: "right" },
              width: { xs: "100%", sm: "auto" },
              marginLeft: "46px",
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontSize: { xs: "16px", sm: "24px", md: "28px" },
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              Hi{" "}
              {user.firstName && user.firstName !== "undefined"
                ? user.firstName
                : "User"}
              , Welcome back
            </Typography>
          </Box>
          <FormikProvider value={formik}>
            <Form
              autoComplete="off"
              id="customDatePicker"
              noValidate
              onSubmit={handleSubmit}
            >
              <Grid
                container
                spacing={2}
                alignItems="center"
                display="flex"
                flexWrap="nowrap"
                sx={{
                  flexDirection: { xs: "row", sm: "row" },
                  justifyContent: "flex-end",
                  gap: { xs: 1, sm: 2 },
                }}
              >
                {customTimeRangeArr.length > 0 &&
                  customTimeRangeArr
                    .filter(({ fieldType }) => fieldType !== "LABEL")
                    .map(
                      (
                        {
                          fieldName,
                          fieldType,
                          label,
                          placeholder,
                          valueType,
                          options,
                        },
                        index
                      ) => {
                        switch (fieldType) {
                          case "DROPDOWN":
                            return (
                              <Grid
                                item
                                xs={12}
                                sm={3}
                                md={2}
                                key={`${fieldName}-${label}-${index}`}
                              >
                                <StyledFormControl
                                  maxwidth={{ xs: "90%", sm: "105px" }}
                                  error={
                                    !!(touched[fieldName] && errors[fieldName])
                                  }
                                >
                                  <Select
                                    id={`${fieldName}-select`}
                                    labelId={`${fieldName}-select`}
                                    sx={{
                                      fontSize: { xs: "12px", sm: "15px" },
                                      fontWeight: "bold",
                                      backgroundColor: controlSurface,
                                      borderRadius: "6px",
                                      border: `1px solid ${outlineColor}`,
                                    }}
                                    value={values[fieldName]}
                                    onChange={timeDropdownOnChangeHandler}
                                    IconComponent={(props) => (
                                      <Box
                                        component="span"
                                        {...props}
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          width: "16px",
                                          height: "16px",
                                          "&::before": {
                                            content: '"▼"', // Downward arrow
                                            fontSize: "12px",
                                          },
                                        }}
                                      />
                                    )}
                                  >
                                    {options.map(
                                      ({ label, value, selected }, index) => {
                                        return (
                                          <MenuItem
                                            value={value}
                                            selected={selected}
                                            key={`${label}-${index}`}
                                          >
                                            {label}
                                          </MenuItem>
                                        );
                                      }
                                    )}
                                  </Select>
                                </StyledFormControl>
                              </Grid>
                            );
                          case "DATE/TIME":
                            return (
                              <Grid
                                item
                                xs={12}
                                sm={3}
                                md={3}
                                key={`${fieldName}-${label}-${index}`}
                              >
                                <LocalizationProvider
                                  dateAdapter={AdapterDateFns}
                                >
                                  <FormControl
                                    sx={{
                                      width: "100%",
                                      borderRadius: "6px",
                                      border: `1px solid ${outlineColor}`,
                                      backgroundColor: controlSurface,
                                    }}
                                  >
                                    <DatePicker
                                      renderInput={(props) => (
                                        <TextField
                                          {...props}
                                          sx={{
                                            fontWeight: "bold",
                                            "& svg": {
                                              width: "15px",
                                              height: "15px",
                                            },
                                            width: "250px",
                                          }}
                                          onBlur={() =>
                                            handleBlurEvent(fieldName)
                                          }
                                          error={
                                            !!(
                                              touched[fieldName] &&
                                              errors[fieldName]
                                            )
                                          }
                                          helperText={
                                            touched[fieldName] &&
                                            errors[fieldName] &&
                                            "Please enter valid date"
                                          }
                                        />
                                      )}
                                      {...getFieldProps(fieldName)}
                                      onClose={() => handleBlurEvent(fieldName)}
                                      name={fieldName}
                                      label={label}
                                      size="small"
                                      disabled={
                                        values["custom_time_dropdown"] !==
                                        "Custom"
                                      }
                                      PopperProps={{ placement: "auto" }}
                                      inputFormat="dd/MM/yyyy"
                                      value={
                                        values[fieldName] === ""
                                          ? null
                                          : values[fieldName]
                                      }
                                      onChange={(newValue) =>
                                        timeIntervalTextChangeHandler(
                                          newValue,
                                          fieldName
                                        )
                                      }
                                    />
                                  </FormControl>
                                </LocalizationProvider>
                              </Grid>
                            );
                          default:
                            return <></>;
                        }
                      }
                    )}
              </Grid>
            </Form>
          </FormikProvider>
        </Stack>

        <Box sx={{ ...dashboardCanvasStyles, marginTop: 2 }}>
          {shouldShowLicenseBanner && (
            <Card
              elevation={0}
              sx={{
                mb: 3,
                borderRadius: 3,
                backgroundColor: cardSurface,
                border: `1px solid ${alpha(
                  theme.palette.divider,
                  theme.palette.mode === "light" ? 0.2 : 0.45
                )}`,
                boxShadow:
                  theme.palette.mode === "light"
                    ? "0px 20px 45px rgba(15, 23, 42, 0.08)"
                    : "0px 20px 45px rgba(0, 0, 0, 0.5)",
              }}
            >
              <Box sx={{ p: { xs: 2, md: 3 } }}>
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} md>
                    <Stack spacing={2}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Icon icon="eva:file-text-outline" width={26} height={26} />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          License Status
                        </Typography>
                      </Stack>

                      <Stack spacing={1}>
                        {expiredLicenses > 0 && (
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Icon icon={alertTriangleFill} color="#ff9800" width={22} />
                            <Typography variant="body2" sx={{ color: "#d32f2f", fontWeight: 500 }}>
                              {expiredLicenses} License
                              {expiredLicenses > 1 ? "s" : ""} need immediate attention
                            </Typography>
                          </Stack>
                        )}

                        {upcomingExpiringLicenses > 0 && (
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Icon icon={clockOutline} color="#ff9800" width={22} />
                            <Typography variant="body2" sx={{ color: "#ff9800", fontWeight: 500 }}>
                              {upcomingExpiringLicenses} Upcoming license
                              {upcomingExpiringLicenses > 1 ? "s" : ""} expiry
                            </Typography>
                          </Stack>
                        )}
                      </Stack>

                      <Box
                        sx={{
                          mt: 1.5,
                          pt: 1.5,
                          borderTop: `1px dashed ${alpha(theme.palette.divider, 0.4)}`,
                        }}
                      >
                        <Typography variant="body2" sx={{ color: supportMutedColor, mb: 0.75 }}>
                          Need help? Contact our support team:
                        </Typography>
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={2}
                          sx={{ color: supportTextColor, fontSize: "0.9rem" }}
                        >
                          <Stack direction="row" spacing={0.75} alignItems="center">
                            <Icon icon="eva:email-outline" width={16} />
                            <Typography variant="body2" sx={{ color: supportTextColor }}>
                              zynkasupport@zynka.ai
                            </Typography>
                          </Stack>
                          <Stack direction="row" spacing={0.75} alignItems="center">
                            <Icon icon="eva:phone-outline" width={16} />
                            <Typography variant="body2" sx={{ color: supportTextColor }}>
                              +919429693773
                            </Typography>
                          </Stack>
                        </Stack>
                      </Box>
                    </Stack>
                  </Grid>

                  <Grid item xs={12} md="auto">
                    <Stack
                      direction={{ xs: "column", sm: "row", md: "column" }}
                      spacing={1}
                      sx={{ width: { xs: "100%", sm: "auto" } }}
                    >
                      {expiredLicenses > 0 && (
                        <Button
                          variant="outlined"
                          size="medium"
                          onClick={handleLicenseStatusClick}
                          sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            borderRadius: 99,
                            width: { xs: "100%", sm: "auto" },
                          }}
                        >
                          Explore
                        </Button>
                      )}
                      <Button
                        variant="contained"
                        size="medium"
                        onClick={handleContactSupport}
                        startIcon={<Icon icon="eva:email-outline" />}
                        sx={{
                          textTransform: "none",
                          fontWeight: 600,
                          borderRadius: 99,
                          width: { xs: "100%", sm: "auto" },
                          backgroundColor: theme.palette.primary.main,
                          color: theme.palette.getContrastText(theme.palette.primary.main),
                          "&:hover": { backgroundColor: theme.palette.primary.dark },
                        }}
                      >
                        Contact Support
                      </Button>
                    </Stack>
                  </Grid>
                </Grid>
              </Box>
            </Card>
          )}
          <Grid container spacing={3}>
            {/* LEFT MAIN CONTENT */}
            <Grid item xs={12} lg={9}>
              <Grid container spacing={3}>
                {statsCards?.map((card, index) => (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={4}
                    key={`${card?.title}-${index}`}
                  >
                    <DashboardNumberStatsCard
                      {...card}
                      bgColor="#FFFFFF"
                      fontColor="grey.700"
                      countColor="black"
                    />
                  </Grid>
                ))}
              </Grid>
            </Grid>

            {/* RIGHT SIDEBAR */}
            <Grid item xs={12} lg={3}>
              <Grid container spacing={3}>
                {/* Donut Card */}
                <Grid item xs={12}>
                  <DashboardDonutStatsCard
                    totalDevices={deviceStats.totalDevices}
                    onlineDevices={deviceStats.onlineDevices}
                    offlineDevices={deviceStats.offlineDevices}
                    isLoading={deviceStats.isLoading}
                  />
                </Grid>

              </Grid>
            </Grid>
          </Grid>

          <Grid container spacing={2} marginTop={0} alignItems="stretch">
            <Grid item xs={12} md={6} sx={{ display: "flex" }}>
              <UsageBarChartCard
                title="Cumulative Usage"
                data={cumulativeUsageData}
                sx={{ flexGrow: 1 }}
              />
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: "flex" }}>
              <DashboardMapCard
                title="Map of Charging Location"
                sx={{ flexGrow: 1 }}
              >
                <AdvancedMapView
                  searchBarEnabled={false}
                  chargers={memoizedChargers}
                  height="100%"
                  dashboardMode={true}
                />
              </DashboardMapCard>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Page>
  );
};

export default DashboardAppNew;
